import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { CalendarCheck, BadgePercent, Ticket, Store, Settings as SettingsIcon } from "lucide-react";

import { AuthProvider, STAFF_ROLES } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import CustomerLayout from "@/components/layout/CustomerLayout";
import { PortalPlaceholder } from "@/components/common/Placeholder";

import Landing from "@/pages/Landing";
import PublicInfo from "@/pages/PublicInfo";
import ContentPage from "@/pages/ContentPage";
import Contact from "@/pages/Contact";
import ClickCollect from "@/pages/ClickCollect";
import VerifyEmail from "@/pages/VerifyEmail";
import Unsubscribe from "@/pages/Unsubscribe";
import Catalogue from "@/pages/Catalogue";
import Basket from "@/pages/Basket";
import Checkout from "@/pages/Checkout";
import ReservationConfirmation from "@/pages/ReservationConfirmation";
import { ShopProvider } from "@/context/ShopContext";
import CategoryPage from "@/pages/CategoryPage";
import ProductDetail from "@/pages/ProductDetail";
import Offers from "@/pages/Offers";
import BrandPage, { BrandsIndex } from "@/pages/BrandPage";

import VipPage from "@/pages/VipPage";
import Stores from "@/pages/Stores";
import StoreDetail from "@/pages/StoreDetail";
import AreasWeServe from "@/pages/AreasWeServe";
import NotFound from "@/pages/NotFound";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import VerifyOtp from "@/pages/auth/VerifyOtp";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AuthCallback from "@/pages/auth/AuthCallback";

import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import Reservations from "@/pages/customer/Reservations";
import Favourites from "@/pages/customer/Favourites";
import Profile from "@/pages/customer/Profile";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminProducts from "@/pages/admin/AdminProducts";
import ProductForm from "@/pages/admin/ProductForm";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminImport from "@/pages/admin/AdminImport";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminUnmatchedMedia from "@/pages/admin/AdminUnmatchedMedia";
import AdminContent from "@/pages/admin/AdminContent";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import AdminPromotionEngine from "@/pages/admin/AdminPromotionEngine";
import AdminReservations from "@/pages/admin/AdminReservations";
import AdminReservationDetail from "@/pages/admin/AdminReservationDetail";
import AdminUpcomingCollections from "@/pages/admin/AdminUpcomingCollections";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminEmailSettings from "@/pages/admin/AdminEmailSettings";
import AdminStores from "@/pages/admin/AdminStores";
import AdminAreas from "@/pages/admin/AdminAreas";
import AdminBankDetails from "@/pages/admin/AdminBankDetails";
import AdminSeo from "@/pages/admin/AdminSeo";

const MANAGERS = ["super_admin", "manager"];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
    <Routes>
      {/* Public */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/fireworks" element={<Catalogue />} />
        <Route path="/basket" element={<Basket />} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/reservation/:number" element={<ProtectedRoute><ReservationConfirmation /></ProtectedRoute>} />
        <Route path="/fireworks/:slug" element={<CategoryPage />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/brands" element={<BrandsIndex />} />
        <Route path="/brands/:slug" element={<BrandPage />} />
        <Route path="/vip" element={<VipPage />} />
        <Route path="/offers" element={<Offers />} />
        <Route path="/click-and-collect" element={<ClickCollect />} />
        <Route path="/stores" element={<Stores />} />
        <Route path="/stores/:slug" element={<StoreDetail />} />
        <Route path="/areas-we-serve" element={<AreasWeServe />} />
        <Route path="/safety" element={<ContentPage slug="firework-safety" fallbackTitle="Firework Safety" />} />
        <Route path="/guides" element={<PublicInfo eyebrow="Learn" title="Buying Guides" subtitle="How to choose the right fireworks for your celebration." phase="Phase 6" />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<ContentPage slug="about" fallbackTitle="About Us" showAreasCta />} />
        <Route path="/faq" element={<ContentPage slug="faq" fallbackTitle="Frequently Asked Questions" />} />
        <Route path="/age-policy" element={<ContentPage slug="age-restricted-sales" fallbackTitle="Age Restricted Sales" />} />
        <Route path="/returns" element={<ContentPage slug="returns-refund-policy" fallbackTitle="Returns & Refunds" />} />
        <Route path="/terms" element={<ContentPage slug="terms-and-conditions" fallbackTitle="Terms & Conditions" />} />
        <Route path="/privacy" element={<ContentPage slug="privacy-policy" fallbackTitle="Privacy Policy" />} />
        <Route path="/cookies" element={<ContentPage slug="cookie-policy" fallbackTitle="Cookie Policy" />} />
        {/* SEO landing pages (permanent, non-yearly URLs) */}
        <Route path="/bonfire-night-fireworks" element={<ContentPage slug="bonfire-night-fireworks" fallbackTitle="Bonfire Night Fireworks" />} />
        <Route path="/new-years-eve-fireworks" element={<ContentPage slug="new-years-eve-fireworks" fallbackTitle="New Year's Eve Fireworks" />} />
        <Route path="/diwali-fireworks" element={<ContentPage slug="diwali-fireworks" fallbackTitle="Diwali Fireworks" />} />
        <Route path="/chinese-new-year-fireworks" element={<ContentPage slug="chinese-new-year-fireworks" fallbackTitle="Chinese New Year Fireworks" />} />
        <Route path="/fireworks-essex" element={<ContentPage slug="fireworks-essex" fallbackTitle="Fireworks in Essex" />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
      </Route>

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-otp" element={<VerifyOtp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Customer portal */}
      <Route path="/dashboard" element={<ProtectedRoute><CustomerLayout /></ProtectedRoute>}>
        <Route index element={<CustomerDashboard />} />
        <Route path="reservations" element={<Reservations />} />
        <Route path="favourites" element={<Favourites />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      {/* Admin portal */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={STAFF_ROLES}><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="reservations" element={<AdminReservations />} />
        <Route path="reservations/:number" element={<AdminReservationDetail />} />
        <Route path="upcoming-collections" element={<AdminUpcomingCollections />} />
        <Route path="inventory" element={<AdminInventory />} />
        <Route path="products" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminProducts /></ProtectedRoute>} />
        <Route path="products/new" element={<ProtectedRoute allowedRoles={MANAGERS}><ProductForm /></ProtectedRoute>} />
        <Route path="products/:id/edit" element={<ProtectedRoute allowedRoles={MANAGERS}><ProductForm /></ProtectedRoute>} />
        <Route path="categories" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminCategories /></ProtectedRoute>} />
        <Route path="brands" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminBrands /></ProtectedRoute>} />
        <Route path="media" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminMedia /></ProtectedRoute>} />
        <Route path="suppliers" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminSuppliers /></ProtectedRoute>} />
        <Route path="imports" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminImport /></ProtectedRoute>} />
        <Route path="media-import" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminUnmatchedMedia /></ProtectedRoute>} />
        <Route path="promotions" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminPromotionEngine /></ProtectedRoute>} />
        <Route path="hero-banners" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminPromotions /></ProtectedRoute>} />
        <Route path="coupons" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminCoupons /></ProtectedRoute>} />
        <Route path="vouchers" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminCoupons /></ProtectedRoute>} />
        <Route path="marketing" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminMarketing /></ProtectedRoute>} />
        <Route path="content" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminContent /></ProtectedRoute>} />
        <Route path="seo" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminSeo /></ProtectedRoute>} />
        <Route path="newsletter" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminNewsletter /></ProtectedRoute>} />
        <Route path="stores" element={<ProtectedRoute allowedRoles={["super_admin"]}><AdminStores /></ProtectedRoute>} />
        <Route path="areas" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminAreas /></ProtectedRoute>} />
        <Route path="bank-details" element={<ProtectedRoute allowedRoles={MANAGERS}><AdminBankDetails /></ProtectedRoute>} />
        <Route path="users" element={<ProtectedRoute allowedRoles={["super_admin"]}><AdminUsers /></ProtectedRoute>} />
        <Route path="email-settings" element={<ProtectedRoute allowedRoles={["super_admin"]}><AdminEmailSettings /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute allowedRoles={["super_admin"]}><PortalPlaceholder icon={SettingsIcon} title="Settings" description="Site settings, store settings and feature toggles." /></ProtectedRoute>} />
      </Route>

      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ShopProvider>
            <AppRouter />
          </ShopProvider>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
